/** Same word split as backend fuzzy_tokens. */
export function fuzzy_tokens(search_term: string): string[] {
  return search_term
    .split(/\s+/)
    .map((part) => part.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, ''))
    .filter((part) => part.length > 0);
}

/** Same limits as backend searchText. One edit, and only for words of 4+ characters. */
const MIN_EDIT_LENGTH = 4;
const MAX_EDIT_DISTANCE = 1;

/**
 * Damerau-Levenshtein distance. SQL Server EDIT_DISTANCE counts insertions,
 * deletions, substitutions, and transpositions the same way.
 */
export function edit_distance(source: string, target: string): number {
  const source_length = source.length;
  const target_length = target.length;
  const distances: number[][] = Array.from({ length: source_length + 1 }, () =>
    Array(target_length + 1).fill(0)
  );

  for (let i = 0; i <= source_length; i += 1) distances[i][0] = i;
  for (let j = 0; j <= target_length; j += 1) distances[0][j] = j;

  for (let i = 1; i <= source_length; i += 1) {
    for (let j = 1; j <= target_length; j += 1) {
      const substitution_cost = source[i - 1] === target[j - 1] ? 0 : 1;
      distances[i][j] = Math.min(
        distances[i - 1][j] + 1,
        distances[i][j - 1] + 1,
        distances[i - 1][j - 1] + substitution_cost
      );
      if (i > 1 && j > 1 && source[i - 1] === target[j - 2] && source[i - 2] === target[j - 1]) {
        distances[i][j] = Math.min(distances[i][j], distances[i - 2][j - 2] + 1);
      }
    }
  }

  return distances[source_length][target_length];
}

function label_words(label: string): string[] {
  return label
    .split(/\s+/)
    .map((word) => word.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''))
    .filter((word) => word.length > 0);
}

function has_digit(token: string): boolean {
  return /[0-9]/.test(token);
}

function token_matches(token: string, label: string, words: string[]): boolean {
  if (label.includes(token)) return true;
  if (token.length < MIN_EDIT_LENGTH || has_digit(token)) return false;
  return words.some((word) => edit_distance(token, word) <= MAX_EDIT_DISTANCE);
}

/**
 * Product picker match. Each typed word must appear in the name or code,
 * or be one edit from a stored word. Pieces of a word still match when they
 * sit inside it, so "but ter" and "f ams" show the product.
 */
export function product_label_matches_search(
  name: string,
  product_code: string,
  search: string
): boolean {
  const tokens = fuzzy_tokens(search).map((token) => token.toLowerCase());
  if (tokens.length === 0) return true;

  const label = `${name} ${product_code}`.toLowerCase();
  const words = label_words(label);
  return tokens.every((token) => token_matches(token, label, words));
}
